import { FormControl, RadioGroup, FormControlLabel, Radio, Typography, Stack, Box } from '@mui/material';
import CheckCircleRoundedIcon from '@mui/icons-material/CheckCircleRounded';
import { Controller, useFormContext } from 'react-hook-form';
import styles from './ProductImageSelect.module.css';
import ZoomInIcon from '@mui/icons-material/ZoomIn';
import Product from '../../../../../@types/Product';
import React from 'react';
import assets from '../../../../../assets';
import useLightBoxContext from '../../../../../hooks/useLightbox';
import useCurrencyConverter from '../../../../../hooks/useCurrencyConverter';

interface ProductImageSelectProps {
  name: string;
  label: string;
  products: Product[];
  defaultValue?: Product;
  displayRetailPrice?: boolean;
  visible?: boolean;
  disabled?: boolean;
  disabledNotice?: string;
  disableImageZoom?: boolean;
  displayPrice?: boolean;
  displayLabel?: boolean;
  imgWidth?: number;
  productAlignment?: 'center' | 'left' | 'right' | 'stretch'
}

const ProductImageSelect = ({
  name,
  label,
  products,
  disabled,
  visible = true,
  disableImageZoom = false,
  displayPrice = true,
  displayLabel = true,
  imgWidth = 175,
  productAlignment = 'center',
  displayRetailPrice = false,
}: ProductImageSelectProps) => {
  const { control } = useFormContext();
  const { convertCurrency } = useCurrencyConverter();
  const { setImages, setOpen } = useLightBoxContext();

  return (
    <Controller
      name={name}
      defaultValue={null}
      control={control}
      render={({ field, fieldState }) => (
        (visible ? (
          <Stack
            direction="column"
            width="100%"
            alignItems="center">
            {displayLabel &&
              <Typography
                sx={{
                  fontSize: '22px',
                  textTransform: 'uppercase',
                  marginBottom: '8px',
                  width: '100%',
                  textAlign: 'center'
                }}>{label}
              </Typography>
            }
            <FormControl fullWidth>
              <RadioGroup
                {...field}
                row
                onChange={(event, value) => {
                  const product = products
                    .find((product) => product.sku === value);
                  field.onChange(product);
                  field.onBlur();
                }}
                value={field.value?.sku ?? null}
                sx={{
                  display: 'flex',
                  flexDirection: 'row',
                  flexWrap: 'wrap',
                  justifyContent: `${productAlignment}`,
                  alignItems: 'stretch'
                }}>
                {products.map((product, index) => (
                  (product.visible ?? true ? (
                    <React.Fragment key={product.sku}>
                      <label className={`${styles.Choice}`}>
                        <div className={styles.ChoiceWrapper}>
                          <Stack
                            width="100%"
                            alignItems="center"
                            justifyContent="center">
                            {!disableImageZoom &&
                              <ZoomInIcon className={styles.ZoomIcon} onClick={() => {
                                if (product.img) setImages([product.img]);
                                if (product.lightBoxImgs) setImages(product.lightBoxImgs);
                                if (!product.img && !product.lightBoxImgs) setImages([]);
                                setOpen(true);
                              }} />
                            }
                            {product.img
                              ? (
                                <img draggable="false"
                                  src={product.img}
                                  alt={`${product.name}`}
                                  width={`${imgWidth}px`} />
                              ) : (
                                <Box
                                  sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    minWidth: `${imgWidth}px`,
                                    minHeight: `${imgWidth}px`,
                                    backgroundColor: '#D9D9D9'
                                  }}>
                                  <img src={assets.images.missingImage} alt='missing' />
                                </Box>
                              )
                            }
                          </Stack>
                          <div className={styles.DetailsWrapper}>
                            <FormControlLabel className={styles.RadioControlLabel}
                              value={product.sku}
                              control={<Radio className={styles.Radio} disabled={disabled} />}
                              label={<></>}
                            />
                            <Stack
                              direction="column"
                              alignItems="center"
                              justifyContent="center"
                              textAlign="center"
                              spacing={0.5}>
                              <Typography variant="secondaryFont" fontWeight={500}>
                                {product.name}
                              </Typography>
                              <Typography variant="secondaryFont">
                                {product.note}
                              </Typography>
                            </Stack>
                            {displayPrice &&
                              <Typography variant="secondaryFont">
                                ({convertCurrency(
                                  displayRetailPrice
                                    ? product.retailPrice ?? product.price
                                    : product.price
                                )})
                              </Typography>
                            }
                            <CheckCircleRoundedIcon className={styles.CheckIcon} />
                          </div>
                        </div>
                      </label>
                    </React.Fragment>
                  ) : (<React.Fragment key={index}></React.Fragment>))
                ))}
              </RadioGroup>
            </FormControl>
            <Typography variant="errorFont">
              {fieldState.error?.message}
            </Typography>
          </Stack >
        ) : (<></>))
      )}
    />
  );
}

export default ProductImageSelect